/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// mantine
import { Box, Paper, Group, Image } from '@mantine/core';

// styles
import classes from './organizations.module.css';

// components
import { Pagination } from '@components/atoms/Pagination';
import { Datatable } from '@components/organisms/Datatable';
import { Button } from '@components/atoms/Button';
import { MenuFilter } from '@components/molecules/MenuFilter/MenuFilter';
import { AddFiltersButton } from '@components/atoms/AddFiltersButton';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';

// i18n
import { useTranslation } from 'react-i18next';

// icons
import { IconCirclePlus } from '@tabler/icons-react';
import ArrowScrollIcon from '@assets/icons/arrow-scroll.svg';

// api
import { useOrganizationsApi } from '@features/resources/organizations/api/get-organizations';
import { useRemove } from '@features/resources/organizations/api/remove-organization';

// configs
import { tableSchemaBase as columns } from '@features/resources/organizations/organizations.columns';

// utils
import { getVisibleColumns } from '@utils/functions';
import { TParsedOrganization } from '@features/resources/organizations/types';
import { useOrganizations } from '@features/resources/organizations/hooks/useOrganizations';

import { TTargetColumn } from '@global-types/index';
import { useCountryCodes } from 'shared/api/get-country-codes';
import isNil from 'lodash/isNil';
import { VERIFIED_FILTER_OPTIONS } from '@utils/options';
import { DrawerOrganization } from '@features/resources/organizations/components/DrawerOrganization';
import { AddOrganization } from '@features/resources/organizations/components/AddOrganization/AddOrganization';
import { ALL_COUNTRIES } from '@config/constants/all-countries';
import { MenuSelectFilter } from '@components/molecules';
import { EditOrganization } from '@features/resources/organizations/components/EditOrganization';
import { useAuthorization } from '@lib/authorization';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const Organizations = () => {
	const { t } = useTranslation();

	const {
		openDrawerEditOrganization,
		showScrollFilters,
		openDrawerAddOrganization,
		handleAddItem,
		ref,
		organizationsDatatableOptions,
		setOrganizationsDatatableOptions,
		containerRef,
		handleScroll,
		openDrawer,
		handleCloseConfirmationDeleteItem,
		handleConfirmDeleteItem,
		handleOpenConfirmationDeleteItem,
		handleViewMore,
		handleRowClicked,
		handleSelectedRecordsChange,
		handleSortStatusChange,
		isOverflowFilters,
		handlepaginationchangesize,
		handlechangepagination,
		organizationsPage,
		organizationsPageSize,
		organizationsOpenConfirmationModalDelete,
		setOrganizationsOpenConfirmationModalDelete,
		organizationsQueryParams,
		organizationsSortStatus,
		organizationsSortParams,
		organizationsSelectedRow,
		handleCountryFilter,
		handleapplystatusfilter,
		handleCloseAddOrganizationDrawer,
		handleCloseEditOrganizationDrawer,
		handleCloseShowMoreOrganizationDrawer,
		handleEditOrganizationDrawer,
		handleEdit,
	} = useOrganizations();

	const {
		data,
		isLoading,
		total: totalItems,
		totalPages,
	} = useOrganizationsApi(
		organizationsPage,
		organizationsPageSize,
		organizationsQueryParams,
		organizationsSortParams
	);

	const remove = useRemove(
		organizationsPage,
		organizationsPageSize
	);

	const { data: countryCodes } = useCountryCodes(
		'organizations'
	);
	const filteredCountries =
		!isNil(countryCodes) && Array.isArray(countryCodes)
			? ALL_COUNTRIES.filter((item) =>
					countryCodes.includes(item.value)
				)
			: [];

	const { checkAcl, checkAccess } = useAuthorization();

	return (
		<Box className={classes.container} ref={ref}>
			{/*Drawer add organization */}
			<AddOrganization
				opened={openDrawerAddOrganization}
				onClose={handleCloseAddOrganizationDrawer}
			/>

			{/*Drawer edit organization */}
			<EditOrganization
				opened={openDrawerEditOrganization}
				onClose={handleCloseEditOrganizationDrawer}
			/>

			{/*Drawer show more organization */}
			<DrawerOrganization
				opened={openDrawer}
				handledelete={handleOpenConfirmationDeleteItem}
				onClose={handleCloseShowMoreOrganizationDrawer}
				data={organizationsSelectedRow}
				handleupdate={handleEditOrganizationDrawer}
			/>

			{/*Delete confirmation modal */}
			<ConfirmationModal
				title={t(
					'organizations.alert.confirmationDelete.title'
				)}
				description={t(
					'organizations.alert.confirmationDelete.description'
				)}
				onConfirm={() => {
					handleConfirmDeleteItem(remove);
				}}
				onClose={handleCloseConfirmationDeleteItem}
				opened={
					organizationsOpenConfirmationModalDelete &&
					organizationsOpenConfirmationModalDelete.open
				}
			/>

			<Paper className={classes.paper}>
				{/* Row filters */}
				<Box className={classes.containerFilters}>
					<Box className={classes.wrapperRowLeft}>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('left');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToLeft
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
						<Box
							ref={containerRef}
							className={classes.wrapperFilters}
						>
							<Group gap={'0px'}>
								<Box
									className={classes.containerFilters5}
								>
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											value={
												organizationsQueryParams[
													'location.countryCode'
												]
													? organizationsQueryParams[
															'location.countryCode'
														]['$in']
													: []
											}
											isPersist
											onApply={handleCountryFilter}
											label={t('global.country')}
											size="lg"
											options={filteredCountries}
											placeholderSearch={t(
												'filters.placeholderSearch.searchCountry'
											)}
											placeholder={t(
												'filters.placeholders.selectCountry'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuSelectFilter
											onApply={handleapplystatusfilter}
											label={t('global.status')}
											size="lg"
											options={VERIFIED_FILTER_OPTIONS}
											placeholder={t(
												'filters.placeholders.selectStatus'
											)}
											withoutSearch
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterNumeric
										}
									>
										<AddFiltersButton>
											{t('global.addFilters')}
										</AddFiltersButton>
									</Box>
								</Box>
							</Group>
						</Box>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('right');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToRight
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
					</Box>
					{checkAcl(ACLS.ORGANIZATIONS.CREATE) &&
						checkAccess({
							allowedRoles: [
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							],
						}) && (
							<div className={classes.wrapperRowRight}>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									hiddenFrom="xxl"
									size="md"
									onClick={handleAddItem}
								>
									{t('organizations.addOrganization')}
								</Button>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									visibleFrom="xxl"
									size="lg"
									onClick={handleAddItem}
								>
									{t('organizations.addOrganization')}
								</Button>
							</div>
						)}
				</Box>

				<Box className={classes.spacerRow} />
				{/* Datatable */}
				<Box className={classes.containerDatatable}>
					<Datatable
						handleUpdateColumns={(
							cols: TTargetColumn[]
						) => {
							setOrganizationsDatatableOptions(
								getVisibleColumns(columns, cols)
							);
						}}
						loading={
							isLoading ? isLoading.toString() : undefined
						}
						allColumns={columns}
						columns={organizationsDatatableOptions}
						data={data}
						onRowClick={handleRowClicked}
						onViewMore={handleViewMore}
						onEditItem={handleEdit}
						onRemoveItem={(row: TParsedOrganization) => {
							setOrganizationsOpenConfirmationModalDelete(
								{
									open: true,
									params: row,
								}
							);
						}}
						style={{
							minHeight: '100%',
						}}
						height={250}
						selectedRecords={null}
						onSelectedRecordsChange={
							handleSelectedRecordsChange
						}
						onSortStatusChange={handleSortStatusChange}
						sortstatus={organizationsSortStatus}
					/>
				</Box>
			</Paper>
			{/* Pagination */}
			<Box className={classes.containerPagination}>
				<Pagination
					pagesize={organizationsPageSize}
					totalitems={totalItems}
					activepage={organizationsPage}
					handlechangesize={handlepaginationchangesize}
					onChange={handlechangepagination}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Box>
	);
};

export default Organizations;
